import { Component, Input, Output, EventEmitter } from '@angular/core';
import { animeDialog, animeBackground, animeSubmodal, animeImgShow } from '../animations';

import { Modal } from '../../model/modal';
import { UsersService } from '../../services/users.service';
import { SitesService } from '../../services/sites.service';
import { DevicesService } from '../../services/devices.service';
import { Options } from '@angular-slider/ngx-slider';
import * as _ from 'lodash-es';
import { lastValueFrom } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BaseComponent } from '@app/directives/base-component';

@Component({
    selector: 'app-site-arm-edit',
    templateUrl: './site-arm-edit.component.html',
    styleUrls: ['./site-arm-edit.component.scss', '../common.scss'],
    animations: [animeDialog, animeBackground, animeSubmodal, animeImgShow],
    standalone: false
})
export class SiteArmEditComponent extends BaseComponent {
  dealerId = this.usersService.dealerId;
  @Input() active: boolean;
  @Input() modal: Modal;
  @Input() site: any = {};
  @Output() onClose: EventEmitter<any> = new EventEmitter();

  enable_modal = false;
  isLoading = false;

  m_warning = new Modal();

  bridges: any[] = [];
  selBridge: any = {};
  _selBridge: any = {};
  isExistBridge = false;

  bridgeHardwareSpecs: any[] = [];
  bridgeArmingDIMap = new Map<string, boolean>();
  disableIndividualArming = false;
  disableAlarmPanelPairing = false;

  siteArmingSources = [
    { value: 2, name: 'Alarm Panel Pairing' },
    { value: 3, name: 'CHeKT Video Security System' },
  ];
  selArmingSource = this.siteArmingSources[0];
  siteArmingMethods = [
    { value: 0, name: 'Individual Bridge Voltage Arming' },
    { value: 1, name: 'Primary Bridge Voltage Arming' },
  ];
  selArmingMethod = this.siteArmingMethods[0];

  armingType = 0; // 0-voltage arming, 1-always armed, 2-always disarmed
  armTrigger = 'no'; // nc-loss of voltage, no-voltage

  sliderOption: Options = {
    floor: 10,
    ceil: 900,
    step: 1,
    showTicks: false,
    showSelectionBar: true,
    animate: false,
    hidePointerLabels: true
  };

  bellSliderOption: Options = {
    floor: 1,
    ceil: 60,
    step: 1,
    showTicks: false,
    showSelectionBar: true,
    animate: false,
    hidePointerLabels: true
  };

  constructor(
    private usersService: UsersService,
    private siteService: SitesService,
    private devicesService: DevicesService,
  ) {
    super();
  }

  ngOnChanges(changes) {
    this.initData();
    if (changes['active'] && this.active) {
      this.enable_modal = true;
      if (this.modal.data['site']) {
        this.setEditData();
        this.loadSiteArmingData();
      }
    }
  }

  initData() {
    this.site = {};
    this.selArmingSource = this.siteArmingSources[1];
    this.selArmingMethod = this.siteArmingMethods[0];
    this.bridges = [];
    this.armTrigger = 'no';
    this.armingType = 0;
    this.selBridge = {};
    this._selBridge = {};
    this.isExistBridge = false;
    this.bridgeHardwareSpecs = [];
    this.bridgeArmingDIMap = new Map<string, boolean>();
    this.disableIndividualArming = false;
    this.disableAlarmPanelPairing = false;
    this.isLoading = false;
  }

  setEditData() {
    this.site = this.modal.data['site'];
    const armingSource = this.site['arming_source'];
    const armingMethod = this.site['arming_method'];

    // arming_source가 1 (legacy Master Bridge Arming) → Alarm Panel Pairing + Primary 매핑
    if (armingSource === 1) {
      this.selArmingSource = this.siteArmingSources[0]; // Alarm Panel Pairing
      this.selArmingMethod = this.siteArmingMethods[1]; // Primary
    } else if (armingSource != null) {
      const source = this.siteArmingSources.find(s => s.value === armingSource);
      if (source) this.selArmingSource = source;
    }

    if (armingMethod != null) {
      const method = this.siteArmingMethods.find(m => m.value === armingMethod);
      if (method) this.selArmingMethod = method;
    }
  }

  // 사이트 디바이스 + hardware spec + bridge별 hardware_version까지 일괄 로드
  async loadSiteArmingData() {
    const dealerId = this.dealerId();
    const siteId = this.site.site_id;
    if (!dealerId || !siteId) return;

    this.isLoading = true;
    try {
      const devices: any[] = (await lastValueFrom(this.siteService.getSiteDevices(dealerId, siteId))) || [];
      this.bridges = devices.filter((d: any) => d.type === 11);
      this.parseBridges(this.bridges);

      const specs: any = await lastValueFrom(this.devicesService.getBridgeHardwareSpec(dealerId, siteId, ''));
      this.bridgeHardwareSpecs = specs || [];

      await this.evaluateArmingDI();
    } catch (err) {
      console.debug('loadSiteArmingData error', err);
    } finally {
      this.isLoading = false;
    }
  }

  // chekt bridge별 hardware_version을 fetch해서 arming_di 보유 여부 판정
  async evaluateArmingDI() {
    const dealerId = this.dealerId();
    const siteId = this.site.site_id;

    const chektBridges = this.bridges.filter(
      (b: any) => b?.registered_by_partner?.toLowerCase() === 'chekt'
    );

    await Promise.all(chektBridges.map(async (b: any) => {
      try {
        const res: any = await lastValueFrom(
          this.devicesService.getBridgeVersion(dealerId, siteId, b.device_id)
        );
        const hardwareVersion = res?.hardware_version;
        const spec = hardwareVersion
          ? this.bridgeHardwareSpecs.find(
              (s: any) => s.model === b.model && s.display_hardware_version === hardwareVersion
            )
          : null;
        this.bridgeArmingDIMap.set(b.device_id, !!spec?.has_arming_di);
      } catch {
        this.bridgeArmingDIMap.set(b.device_id, false);
      }
    }));

    // 하나라도 arming_di가 없으면 Individual 비활성화
    this.disableIndividualArming = chektBridges.some(
      (b: any) => !this.bridgeArmingDIMap.get(b.device_id)
    );

    // 모든 bridge에 arming_di가 없으면 Alarm Panel Pairing 자체 비활성화
    this.disableAlarmPanelPairing = chektBridges.length > 0
      && chektBridges.every((b: any) => !this.bridgeArmingDIMap.get(b.device_id));

    // 강제 전환 처리
    if (this.disableAlarmPanelPairing && this.selArmingSource?.value === 2) {
      const vss = this.siteArmingSources.find(s => s.value === 3);
      if (vss) {
        this.selArmingSource = vss;
        this.setArmingSource();
      }
    }
    if (this.disableIndividualArming && this.selArmingMethod?.value === 0) {
      const primary = this.siteArmingMethods.find(m => m.value === 1);
      if (primary) this.selArmingMethod = primary;
    }
    if (this.selArmingMethod?.value === 1) {
      this.autoSelectValidPrimaryBridge();
    }
  }

  // 현재 선택된 primary bridge에 arming_di가 없으면 arming_di 있는 bridge로 교체
  autoSelectValidPrimaryBridge() {
    const currentId = this.selBridge?.device_id;
    if (currentId && this.bridgeArmingDIMap.get(currentId)) return;

    const candidate = this.bridges.find((b: any) => this.bridgeArmingDIMap.get(b.device_id));
    if (candidate) {
      this.selBridge = _.cloneDeep(candidate);
    }
  }

  compareBridges(b1: any, b2: any): boolean {
    return b1?.device_id === b2?.device_id;
  }

  parseBridges(bridges: any[]) {
    bridges.forEach((b: any) => {
      b.bellDurationMin = b.bell_duration / 60;
    });
    this.isExistBridge = bridges.length !== 0;

    let masterBridge = bridges.find((b: any) => b.is_main_device === 1);
    if (!masterBridge && this.isExistBridge) {
      const sorted = [...bridges].sort((a, b) => a.virtual_zone - b.virtual_zone);
      masterBridge = sorted[0];
    }

    if (masterBridge) {
      this._selBridge = masterBridge;
    } else if (bridges.length !== 0) {
      this._selBridge = bridges[0];
    } else {
      this._selBridge = {
        entry_delay: 60,
        exit_delay: 60,
        bell_duration: 300,
        bellDurationMin: 5
      };
    }
    this.selBridge = _.cloneDeep(this._selBridge);
  }

  setArmingSource() {
    if (this.selArmingSource.value === 3) {
      this.parseBridges(this.bridges);
    }
  }

  setArmingMethod() {
    if (this.selArmingMethod?.value === 1) {
      this.autoSelectValidPrimaryBridge();
    }
  }

  setBellDuration() {
    this.selBridge.bell_duration = this.selBridge.bellDurationMin * 60;
  }

  selectArmingType(armingType: number, armTrigger: string = null) {
    this.armingType = armingType;
    this.armTrigger = armTrigger;
  }

  setArmingType(bridgeId?: any) {
    const bridges = this.bridges;
    const armingType = this.armingType;
    const armTrigger = armingType === 0 ? this.armTrigger : null;

    if (bridgeId) {
      const mainBridge = bridges.find((bridge: any) => bridge.device_id + '' === bridgeId + '');
      if (mainBridge) {
        mainBridge.dealer_id = this.dealerId();
        this.devicesService.putDeviceArm(mainBridge, armingType, armTrigger)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {},
            error: (err) => this.errorDialog(err, 'Update error'),
          });
      }
    } else {
      bridges.forEach((bridge: any) => {
        bridge.dealer_id = this.dealerId();
        this.devicesService.putDeviceArm(bridge, armingType, armTrigger)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {},
            error: (err) => this.errorDialog(err, 'Update error'),
          });
      });
    }
  }

  async update() {
    this.isLoading = true;
    const dealerId = this.dealerId();
    const siteId = this.site['site_id'];
    let selArmingSource = this.selArmingSource.value;

    if (selArmingSource === 3) {
      this.setBellDuration();
    }

    let isChangePrimaryBridgeForAlarmPanel = false;
    if (this.selArmingSource.value === 2 && this.selArmingMethod.value === 1) {
      selArmingSource = 1;
      // Primary bridge가 변경된 경우 마스터 브릿지 변경
      isChangePrimaryBridgeForAlarmPanel = !!(
        this._selBridge?.device_id
        && this.selBridge?.device_id
        && this._selBridge.device_id !== this.selBridge.device_id
      );
    }

    const data: any = {
      arming_source: selArmingSource // 1: Legacy Master, 2: Alarm Panel Pairing, 3: CHeKT VSS
    };
    if (selArmingSource === 3 && this.isExistBridge) {
      data.entry_delay = this.selBridge.entry_delay || 10;
      data.exit_delay = this.selBridge.exit_delay || 10;
      data.bell_duration = this.selBridge.bell_duration || 60;
    }

    try {
      const res: any = await lastValueFrom(this.siteService.putProgramming(dealerId, siteId, data));
      const armingSource = res.arming_source;
      this.site['arming_source'] = selArmingSource;

      let mainBridgeId = null;
      if (armingSource === 1 || armingSource === 2) {
        if (armingSource === 1) mainBridgeId = res.main_device_id;
        this.setArmingType(mainBridgeId);
      }

      if (isChangePrimaryBridgeForAlarmPanel) {
        await lastValueFrom(
          this.devicesService.updateSiteMasterBridge(dealerId, siteId, this.selBridge.device_id, true)
        );
      }

      this.confirmDialog('Success', 'Edit Arming Configuration', 'done', 'green');
    } catch (err: any) {
      setTimeout(() => {
        const msg = err?.status === 403 ? 'Permission Denied' : 'Faild';
        this.confirmDialog(msg, 'Edit Arming Configuration', 'warning', '#f1556c');
      }, 200);
    } finally {
      this.isLoading = false;
    }
  }

  confirmDialog(header = '', msg = '', icon = 'done', color = '#1abc9c') {
    this.m_warning.data = {
      header,
      contents: `<p>${msg}</p>`,
      submit_btn: 'OK',
      submit_class: ['button-primary'],
      icon,
      isConfirm: true,
      color,
    };
    this.m_warning.data['submit_func'] = () => {
      if (color === 'green') this.close();
    };
    this.m_warning.open();
  }

  errorDialog(err: any, title: string) {
    let msg = 'failed.';
    if (err?.error?.message) msg = err.error.message;
    if (err?._body) {
      try { msg = JSON.parse(err._body).message; } catch {}
    }
    setTimeout(() => {
      this.confirmDialog(title, msg, 'warning', 'orange');
    }, 200);
    this.isLoading = false;
  }

  close(result?: any) {
    this.onClose.next(result ? result : { res: false });
    this.modal.close();
    setTimeout(() => {
      this.enable_modal = false;
    }, 200);
  }
}
